import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { setAccountDisabled } from "../../services/dataService";
import { logUserActivity } from "../../utils/activityLogger";

const actionButtonSx = { padding: "8px 32px", fontSize: 15, fontWeight: 500 };

// Shared by Students.js and Tutors.js (phase-2 task P2), same placement idiom as
// AdjustBalanceDialog (W2): one dialog, opened from either drawer's Ban/Unban button.
// `person` is whatever the drawer currently has selected — needs only `id`, `name` and
// `disabled`. `onDone` is the caller's own refetch (e.g. re-running its openDrawer), same
// as AdjustBalanceDialog's `onAdjusted`, so the drawer picks up the flipped flag.
//
// Unlike AdjustBalanceDialog this has no form: the only input is the confirmation
// itself, so there is nothing to validate and nothing to reset on close.
const BanAccountDialog = ({ open, onClose, person, actingUser, onDone }) => {
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const wasDisabled = !!person?.disabled;
  // The action this confirm always performs: flip whatever the drawer currently shows.
  const nextDisabled = !wasDisabled;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const submit = async () => {
    if (!person) return;
    setSubmitting(true);
    try {
      await setAccountDisabled({ userId: person.id, disabled: nextDisabled });
      await logUserActivity({
        userId: actingUser?.uid || actingUser?.id || "unknown",
        userName: actingUser?.name || actingUser?.displayName || "Unknown User",
        userEmail: actingUser?.email || "unknown@example.com",
        action: nextDisabled ? "ACCOUNT_DISABLE" : "ACCOUNT_ENABLE",
        entityType: "user",
        entityId: person.id,
        entityName: person.name,
        before: { disabled: wasDisabled },
        after: { disabled: nextDisabled },
        description: `${actingUser?.name || "User"} ${
          nextDisabled ? "disabled" : "enabled"
        } ${person.name}'s account`,
      });
      notify(`${nextDisabled ? "Disabled" : "Enabled"} ${person.name}'s account.`);
      onClose();
      if (onDone) await onDone();
    } catch (e) {
      notify(e.message || "Failed to change account status.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>
          {nextDisabled ? "Disable" : "Enable"} {person?.name || "this account"}'s account?
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            {nextDisabled
              ? "They will not be able to sign in."
              : "They will be able to sign in again."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting} sx={actionButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={nextDisabled ? "error" : "success"}
            disabled={submitting}
            onClick={submit}
            sx={actionButtonSx}
          >
            {submitting
              ? nextDisabled ? "Disabling..." : "Enabling..."
              : nextDisabled ? "Disable" : "Enable"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BanAccountDialog;
